'use client'

import type { UseVisibilityTrackedEventOptions } from '@maestro/commerce-blocks/hooks/hooks.types'

import { useEffect, useRef } from 'react'

export const VIEW_EVENT_DELAY = Number(
  process.env.NEXT_PUBLIC_VIEW_EVENT_DELAY ?? 600
)

export function useVisibilityTrackedEvent(
  props: UseVisibilityTrackedEventOptions
) {
  const {
    deduplicationKey,
    delay = VIEW_EVENT_DELAY,
    threshold = 0.5,
    enabled = true,
    onVisible,
  } = props
  const containerRef = useRef<HTMLDivElement>(null)
  const firedKeysRef = useRef<Set<string>>(new Set())
  const timerRef = useRef<NodeJS.Timeout | null>(null)
  const onVisibleRef = useRef(onVisible)

  // Keep callback ref fresh without triggering effect re-runs
  useEffect(() => {
    onVisibleRef.current = onVisible
  }, [onVisible])

  useEffect(() => {
    // Cancel any pending timer when key changes (search-as-you-type)
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }

    const element = containerRef.current
    if (!enabled || !element) {
      return
    }

    if (firedKeysRef.current.has(deduplicationKey)) {
      return
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          timerRef.current = setTimeout(() => {
            if (!firedKeysRef.current.has(deduplicationKey)) {
              firedKeysRef.current.add(deduplicationKey)
              onVisibleRef.current()
            }
            timerRef.current = null
          }, delay)
        } else if (timerRef.current) {
          clearTimeout(timerRef.current)
          timerRef.current = null
        }
      },
      { threshold }
    )

    observer.observe(element)

    return () => {
      observer.disconnect()
      if (timerRef.current) {
        clearTimeout(timerRef.current)
        timerRef.current = null
      }
    }
  }, [deduplicationKey, delay, threshold, enabled])

  return { containerRef }
}
